import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Box, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import PriceRoutePnLDisplay from "./PriceRoutePnLDisplay";
import {
  getPriceRouteData,
  getPriceRouteFilterOptions,
  startAsyncPriceRoutePnlDownload,
  getDownloadProgress,
  downloadFile,
} from "../../../viewServices/calls/apiCalls";
import { toast } from "react-toastify";
import AuthContext from "../../../../../connections/auth/AuthProvider";
import PropTypes from "prop-types";

const priceRouteViewStyle = (theme) => ({
  containerBox: {
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    height: "100%",
    overflow: "hidden",
  },
  controlsContainer: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    gap: theme.spacing(2),
  },
});

PriceRouteView.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  isLoading: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  selectedVolume: PropTypes.string.isRequired,
  selectedSolverStatus: PropTypes.string,
  onFilterOptionsChange: PropTypes.func,
};

export default function PriceRouteView({
  scenarios,
  folders = [],
  isLoading = false,
  filters,
  selectedVolume,
  selectedSolverStatus,
  onFilterOptionsChange,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  const useStyles = makeStyles(priceRouteViewStyle);
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // State management
  const [pnlData, setPnlData] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errors, setErrors] = useState({});
  const [drillDownsOpen, setDrillDownsOpen] = useState({}); // Per-scenario drill-down state
  const [selectedScenarios, setSelectedScenarios] = useState({
    0: null,
    1: null,
  });

  // Track previous scenarios to detect changes
  const prevScenariosRef = useRef({
    0: null,
    1: null,
  });

  // Track previous filters to detect filter changes
  const prevFiltersRef = useRef(filters);

  // Track previous volume selection
  const prevVolumeRef = useRef(selectedVolume);

  // Route filter state
  const [routeFilterOptions, setRouteFilterOptions] = useState({
    node1_reference_productionsite: [],
    node1_location: [],
    node2_reference_client: [],
    node2_location: [],
    node3_reference_pointofsale: [],
    node3_location: [],
  });

  // Route filters - per scenario (like drillDownsOpen)
  const [routeFilters, setRouteFilters] = useState({});

  // Track previous route filters to detect changes - now per scenario
  const prevRouteFiltersRef = useRef({});

  // Fetch filter options when scenarios change
  useEffect(() => {
    const fetchFilterOptions = async () => {
      const scenarioIds = Object.values(selectedScenarios)
        .filter((s) => s !== null)
        .map((s) => s.scenario_id);

      if (scenarioIds.length === 0) {
        // No scenarios selected - send empty object to disable controls
        if (onFilterOptionsChange) {
          onFilterOptionsChange({});
        }
        setRouteFilterOptions({
          node1_reference_productionsite: [],
          node1_location: [],
          node2_reference_client: [],
          node2_location: [],
          node3_reference_pointofsale: [],
          node3_location: [],
        });
        return;
      }

      try {
        console.log(
          "🎯 Tax Route: Fetching filter options for scenarios:",
          scenarioIds,
        );
        const options = await getPriceRouteFilterOptions(scenarioIds);
        console.log("✅ Tax Route: Got filter options:", options);

        // Report standard filters AND solver_status to parent
        if (onFilterOptionsChange) {
          onFilterOptionsChange({
            products: options.products || [],
            brands: options.brands || [],
            channels: options.channels || [],
            regions: options.regions || [],
            clients: options.clients || [],
            solver_status: options.solver_status || [],
          });
        }

        // Store route filter options locally
        setRouteFilterOptions({
          node1_reference_productionsite:
            options.node1_reference_productionsite || [],
          node1_location: options.node1_location || [],
          node2_reference_client: options.node2_reference_client || [],
          node2_location: options.node2_location || [],
          node3_reference_pointofsale:
            options.node3_reference_pointofsale || [],
          node3_location: options.node3_location || [],
        });
      } catch (error) {
        console.error("❌ Tax Route: Failed to fetch filter options:", error);
        // Clear options on error - send empty object to disable controls
        if (onFilterOptionsChange) {
          onFilterOptionsChange({});
        }
        setRouteFilterOptions({
          node1_reference_productionsite: [],
          node1_location: [],
          node2_reference_client: [],
          node2_location: [],
          node3_reference_pointofsale: [],
          node3_location: [],
        });
      }
    };

    fetchFilterOptions();
  }, [selectedScenarios]); // onFilterOptionsChange intentionally excluded to prevent infinite loop

  // Load PnL data for a slot
  const loadPnlData = useCallback(
    async (slotIndex) => {
      const scenario = selectedScenarios[slotIndex];
      if (!scenario) return;

      const scenarioId = scenario.scenario_id;
      setLoadingStates((prev) => ({ ...prev, [slotIndex]: true }));
      setErrors((prev) => ({ ...prev, [slotIndex]: null }));

      try {
        // Combine standard filters and slot-specific route filters
        const slotRouteFilters = routeFilters[slotIndex] || {};
        const allFilters = {
          ...filters,
          ...slotRouteFilters,
        };

        // Add solver_status if selected
        if (selectedSolverStatus) {
          allFilters.solver_status = selectedSolverStatus;
        }

        // Use real API call with weight field and all filters
        // Pass selectedVolume directly - no mapping needed
        const data = await getPriceRouteData(
          scenarioId,
          allFilters,
          selectedVolume, // Direct: "input_sellthrough_volume_sales", "input_sellin_volume_sales", or "1"
        );

        // Initialize drill-downs for this specific slot
        // Default: ELO headers expanded, all other rows collapsed
        setDrillDownsOpen((prev) => {
          if (!prev[slotIndex]) {
            // Find all ELO header row indices (is_header: true)
            const eloHeaderIndices = [];
            if (data.plStructure && data.plStructure.current) {
              data.plStructure.current.forEach((row) => {
                if (row.is_header && row.row_index !== undefined) {
                  eloHeaderIndices.push(row.row_index);
                }
              });
            }
            return {
              ...prev,
              [slotIndex]: eloHeaderIndices, // ELO headers expanded, others collapsed
            };
          }
          return prev;
        });

        setPnlData((prev) => ({
          ...prev,
          [slotIndex]: data,
        }));
      } catch (error) {
        setErrors((prev) => ({
          ...prev,
          [slotIndex]: error.message || t("overview.pnl_load_error"),
        }));
        toast.error(
          t("overview.pnl_load_error_for_scenario", { scenario: scenarioId }),
        );
      } finally {
        setLoadingStates((prev) => ({
          ...prev,
          [slotIndex]: false,
        }));
      }
    },
    [
      filters,
      routeFilters,
      selectedVolume,
      selectedSolverStatus,
      t,
      selectedScenarios,
    ],
  );

  // Track previous solver status
  const prevSolverStatusRef = useRef(selectedSolverStatus);

  // Detect filter, volume, or solver status changes and reload all active slots
  useEffect(() => {
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    const volumeChanged = prevVolumeRef.current !== selectedVolume;
    const solverStatusChanged =
      prevSolverStatusRef.current !== selectedSolverStatus;

    if (filtersChanged || volumeChanged || solverStatusChanged) {
      prevFiltersRef.current = filters;
      prevVolumeRef.current = selectedVolume;
      prevSolverStatusRef.current = selectedSolverStatus;

      // Reload data for all slots when global filters, volume, or solver status change
      Object.keys(selectedScenarios).forEach((slotIndex) => {
        if (selectedScenarios[slotIndex]) {
          loadPnlData(slotIndex);
        }
      });
    }
  }, [
    filters,
    selectedVolume,
    selectedSolverStatus,
    loadPnlData,
    selectedScenarios,
  ]);

  // Separate effect for route filters - only reload the specific slot that changed
  useEffect(() => {
    Object.keys(routeFilters).forEach((slotIndex) => {
      const prevFilters = prevRouteFiltersRef.current[slotIndex] || {};
      const currentFilters = routeFilters[slotIndex] || {};

      const prevJson = JSON.stringify(prevFilters);
      const currentJson = JSON.stringify(currentFilters);

      console.log(`🔍 Checking route filters for slot ${slotIndex}:`, {
        prevJson,
        currentJson,
        changed: prevJson !== currentJson,
      });

      // Check if this slot's filters actually changed
      if (prevJson !== currentJson) {
        console.log(
          `🔄 Route filters changed for slot ${slotIndex}, reloading...`,
        );
        loadPnlData(slotIndex);
      }
    });

    // Update ref after all checks
    prevRouteFiltersRef.current = { ...routeFilters };
  }, [routeFilters, loadPnlData]);

  // Detect scenario changes and load data only for new/changed slots
  useEffect(() => {
    Object.entries(selectedScenarios).forEach(([slotIndex, scenario]) => {
      const prevScenario = prevScenariosRef.current[slotIndex];

      // Only load if scenario changed for this slot
      if (scenario && scenario.scenario_id !== prevScenario?.scenario_id) {
        loadPnlData(slotIndex);
      }
    });

    // Update ref after processing
    prevScenariosRef.current = { ...selectedScenarios };
  }, [selectedScenarios, loadPnlData]);

  const handleDownload = async (scenario) => {
    try {
      const fullTable = false;
      const downloadJob = await startAsyncPriceRoutePnlDownload(
        scenario.scenario_id,
        fullTable,
      );

      const downloadId = downloadJob.download_id;
      const progressToastId = `download-progress-${downloadId}`;

      toast.info(
        <div>
          <div style={{ marginBottom: "8px" }}>
            Starting P&L dataset download...
          </div>
          <div
            style={{
              width: "100%",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
              height: "8px",
            }}
          >
            <div
              style={{
                width: "0%",
                backgroundColor: clientColor,
                height: "100%",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <div
            style={{
              fontSize: "0.8em",
              marginTop: "4px",
              color: "#666",
            }}
          >
            0% complete
          </div>
        </div>,
        {
          toastId: progressToastId,
          autoClose: false,
          closeButton: false,
          position: "top-right",
        },
      );

      const pollInterval = setInterval(async () => {
        try {
          const progress = await getDownloadProgress(downloadId);

          toast.update(progressToastId, {
            render: (
              <div>
                <div style={{ marginBottom: "8px" }}>{progress.stage}</div>
                <div
                  style={{
                    width: "100%",
                    backgroundColor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "8px",
                  }}
                >
                  <div
                    style={{
                      width: `${progress.progress}%`,
                      backgroundColor: clientColor,
                      height: "100%",
                      borderRadius: "4px",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.8em",
                    marginTop: "4px",
                    color: "#666",
                  }}
                >
                  {Math.round(progress.progress)}% complete
                </div>
              </div>
            ),
            type: "info",
            autoClose: false,
            closeButton: false,
          });

          if (progress.status === "success" && progress.file_ready) {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    Download ready! Click to dismiss.
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#4caf50",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    100% complete
                  </div>
                </div>
              ),
              type: "success",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });

            try {
              const fileBlob = await downloadFile(downloadId);
              const url = URL.createObjectURL(fileBlob);
              const a = document.createElement("a");
              a.href = url;
              let filename = `pnl_scenario_${scenario.scenario_id}`;
              a.download = `${filename}.xlsx`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch (fileErr) {
              console.error("Error downloading P&L file:", fileErr);
              toast.error("Failed to download file. Please try again.");
            }
          } else if (progress.status === "error") {
            clearInterval(pollInterval);
            toast.update(progressToastId, {
              render: (
                <div>
                  <div style={{ marginBottom: "8px" }}>
                    Download failed: {progress.error_message}
                  </div>
                  <div
                    style={{
                      width: "100%",
                      backgroundColor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        backgroundColor: "#f44336",
                        height: "100%",
                        borderRadius: "4px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: "0.8em",
                      marginTop: "4px",
                      color: "#666",
                    }}
                  >
                    Error occurred
                  </div>
                </div>
              ),
              type: "error",
              autoClose: false,
              closeButton: true,
              closeOnClick: true,
            });
          }
        } catch (error) {
          clearInterval(pollInterval);
          console.error("Error checking download progress:", error);
          toast.error("Failed to check download progress");
        }
      }, 2000);
    } catch (error) {
      console.error("Error starting P&L download:", error);
      toast.error("Failed to start download. Please try again.");
    }
  };

  // Handle removing a PNL card
  const handleRemovePnl = (slotIndex) => {
    const scenario = selectedScenarios[slotIndex];
    if (!scenario) return;

    // Clean up all data for this slot
    setPnlData((prev) => {
      const newData = { ...prev };
      delete newData[slotIndex];
      return newData;
    });

    setSelectedScenarios((prev) => ({
      ...prev,
      [slotIndex]: null,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[slotIndex];
      return newErrors;
    });

    setLoadingStates((prev) => {
      const newLoadingStates = { ...prev };
      delete newLoadingStates[slotIndex];
      return newLoadingStates;
    });

    // Delete drill-downs for this slot
    setDrillDownsOpen((prev) => {
      const newDrillDowns = { ...prev };
      delete newDrillDowns[slotIndex];
      return newDrillDowns;
    });

    // Delete route filters for this slot
    setRouteFilters((prev) => {
      const newRouteFilters = { ...prev };
      delete newRouteFilters[slotIndex];
      return newRouteFilters;
    });
  };

  // Create a setter function for drill-downs specific to a slot
  const createSetDrillDownsOpenForSlot = (slotIndex) => {
    return (updater) => {
      setDrillDownsOpen((prev) => {
        const currentDrillDowns = prev[slotIndex] || [];
        const newDrillDowns =
          typeof updater === "function" ? updater(currentDrillDowns) : updater;
        return {
          ...prev,
          [slotIndex]: newDrillDowns,
        };
      });
    };
  };

  // Shared mouse-over state for synchronized highlighting across both PnLs
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const handleScenarioChange = (slotIndex, newScenario) => {
    setSelectedScenarios((prev) => ({
      ...prev,
      [slotIndex]: newScenario,
    }));

    if (!newScenario) {
      handleRemovePnl(slotIndex);
    }
  };

  // Route filter handlers - now per slot
  const createApplyRouteFiltersForSlot = (slotIndex) => {
    return (newFilters) => {
      console.log(
        `🔧 Applying route filters for slot ${slotIndex}:`,
        newFilters,
      );
      setRouteFilters((prev) => ({
        ...prev,
        [slotIndex]: newFilters,
      }));
      // loadPnlData will be triggered by the useEffect watching routeFilters
    };
  };

  const createClearRouteFiltersForSlot = (slotIndex) => {
    return () => {
      console.log(`🧹 Clearing route filters for slot ${slotIndex}`);
      setRouteFilters((prev) => ({
        ...prev,
        [slotIndex]: {
          node1_reference_productionsite: "",
          node1_location: "",
          node2_reference_client: "",
          node2_location: "",
          node3_reference_pointofsale: "",
          node3_location: "",
        },
      }));
      // loadPnlData will be triggered by the useEffect watching routeFilters
    };
  };

  return (
    <Box className={classes.containerBox}>
      <PriceRoutePnLDisplay
        selectedScenarios={selectedScenarios}
        scenarios={scenarios}
        folders={folders}
        isScenariosLoading={isLoading}
        pnlData={pnlData}
        loadingStates={loadingStates}
        errors={errors}
        drillDownsOpen={drillDownsOpen}
        clientColor={clientColor}
        onScenarioSelect={handleScenarioChange}
        createSetDrillDownsOpenForSlot={createSetDrillDownsOpenForSlot}
        handleDownload={handleDownload}
        loadPnlData={loadPnlData}
        hoveredRowIndex={hoveredRowIndex}
        setHoveredRowIndex={setHoveredRowIndex}
        routeFilterOptions={routeFilterOptions}
        routeFilters={routeFilters}
        createApplyRouteFiltersForSlot={createApplyRouteFiltersForSlot}
        createClearRouteFiltersForSlot={createClearRouteFiltersForSlot}
      />
    </Box>
  );
}
